/*
  EVER LEAF — PORTRAIT-ONLY PHONE EXPERIENCE

  A web page cannot reliably force browser orientation (iPhone Safari offers no
  orientation lock to an ordinary page at all), so the RELIABLE behaviour is:
  portrait renders the normal site, and a phone in landscape gets a blocking
  overlay. `screen.orientation.lock()` is attempted only as a silent
  enhancement where a browser genuinely allows it without fullscreen.

  THE KEYBOARD PROBLEM, AND WHY THIS FILE NEVER MEASURES THE VIEWPORT.
  The obvious test - is the viewport wider than it is tall - misfires badly.
  On a short phone the on-screen keyboard can take enough of the LAYOUT
  viewport that a portrait page genuinely becomes wider than it is tall (e.g.
  360x640 minus a ~340px keyboard leaves 360x300), and `visualViewport` shrinks
  on every platform. Typing in Line 1 or Line 2 would then be read as
  "landscape" and the customer would be told to rotate a phone they are already
  holding upright. So orientation is taken ONLY from the device itself -
  `screen.orientation.type`, then `window.orientation`, then a comparison of
  `screen` (not viewport) dimensions. Every one of those is unaffected by the
  keyboard.

  This module never reloads, re-renders or re-initialises anything, and never
  touches scroll position or `body` overflow - so returning to portrait leaves
  the page, the personalizer, the Carousel page, the View Toggle state, the
  uploaded photo and the scroll offset exactly as they were.
*/
(function () {
  'use strict';

  // A phone's SHORT edge. Tablets are excluded by this (an iPad's short edge is
  // 768+), and it is orientation-independent because it takes the minimum.
  var PHONE_MAX_SHORT_EDGE = 500;

  var overlay = null;
  var inerted = [];
  var lastBlocked = null;

  function mq(q) {
    return !!(window.matchMedia && window.matchMedia(q).matches);
  }

  /* A coarse pointer with no hover is what separates a phone from a desktop
     window - a desktop monitor stays hover-capable no matter how narrow the
     window is dragged, so an ordinary landscape desktop can never be caught
     here. Combined with the short-edge cap, this is the project's existing
     "real touch device" idea rather than a new device database. */
  function isPhone() {
    if (!mq('(hover: none) and (pointer: coarse)')) return false;
    var s = window.screen || {};
    var w = s.width || window.innerWidth || 0;
    var h = s.height || window.innerHeight || 0;
    if (!w || !h) return false;
    return Math.min(w, h) <= PHONE_MAX_SHORT_EDGE;
  }

  // Device orientation only - see the keyboard note in the file header.
  function isLandscape() {
    var so = window.screen && window.screen.orientation;
    if (so && typeof so.type === 'string') return so.type.indexOf('landscape') === 0;
    if (typeof window.orientation === 'number') return Math.abs(window.orientation) === 90;
    var s = window.screen;
    if (s && s.width && s.height) return s.width > s.height;
    return false;
  }

  function getOverlay() {
    if (overlay && overlay.isConnected) return overlay;
    overlay = document.querySelector('[data-ever-leaf-portrait-only]');
    return overlay;
  }

  /* Pointer interaction is already impossible while the overlay covers the
     viewport, but an unblocked page would still be reachable by keyboard and
     by assistive technology. `inert` removes a subtree from hit-testing, the
     tab order and the accessibility tree in one step. Only nodes THIS module
     set are ever cleared again, so a drawer that is inert for its own reasons
     is left alone; where `inert` is unsupported the assignment is an inert
     no-op and the overlay alone still blocks pointers. */
  function setPageInert(on) {
    var host = document.body;
    if (!host) return;
    if (on) {
      if (inerted.length) return;
      var ov = getOverlay();
      Array.prototype.forEach.call(host.children, function (node) {
        if (node === ov || node.contains(ov)) return;
        if (node.inert) return;
        try {
          node.inert = true;
          inerted.push(node);
        } catch (e) { /* unsupported - the overlay still covers the viewport */ }
      });
      return;
    }
    inerted.forEach(function (node) {
      try { node.inert = false; } catch (e) { /* see above */ }
    });
    inerted = [];
  }

  function apply() {
    var ov = getOverlay();
    if (!ov) return;
    var blocked = isPhone() && isLandscape();
    if (blocked === lastBlocked) return;
    lastBlocked = blocked;
    document.documentElement.setAttribute('data-ever-leaf-portrait-block', blocked ? 'true' : 'false');
    ov.setAttribute('aria-hidden', blocked ? 'false' : 'true');
    setPageInert(blocked);
    if (blocked) {
      // Dismiss the keyboard if it was open, and move focus onto the message.
      var active = document.activeElement;
      if (active && active !== document.body && typeof active.blur === 'function') active.blur();
      if (typeof ov.focus === 'function') {
        try { ov.focus({ preventScroll: true }); } catch (e) { /* older browsers */ }
      }
    }
  }

  /* Enhancement only (2E): most browsers reject this outright without an
     active fullscreen request, which is exactly why the overlay above is the
     mechanism that actually matters. Never requests fullscreen, and every
     failure path is swallowed. */
  function tryLock() {
    var so = window.screen && window.screen.orientation;
    if (!so || typeof so.lock !== 'function') return;
    try {
      var p = so.lock('portrait');
      if (p && typeof p.catch === 'function') p.catch(function () { /* expected */ });
    } catch (e) { /* expected */ }
  }

  function init() {
    if (!getOverlay()) return;
    apply();
    tryLock();

    var so = window.screen && window.screen.orientation;
    if (so && typeof so.addEventListener === 'function') so.addEventListener('change', apply);
    // Legacy iOS and anything without screen.orientation events.
    window.addEventListener('orientationchange', apply);
    /* `resize` is safe here despite firing for the keyboard: apply() re-derives
       everything from device orientation, so a keyboard-driven resize simply
       recomputes the same answer. It is kept because some browsers report a
       rotation only through resize. */
    window.addEventListener('resize', apply);
    if (window.matchMedia) {
      var m = window.matchMedia('(orientation: landscape)');
      if (typeof m.addEventListener === 'function') m.addEventListener('change', apply);
      else if (typeof m.addListener === 'function') m.addListener(apply); // eslint-disable-line no-restricted-properties -- Safari <14
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
